import clsx from "clsx";
import { useEffect, useState, useMemo, useRef } from "react";
import * as d3 from "d3";
import { geoRobinson } from "d3-geo-projection";
import { Select } from "@/components/Select";
import { Tooltip, useTooltip } from "@/components/Tooltip";
import sectorOptions from "@/config/map/sectors";
import { useFilter } from "@/hooks/useFilter";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { getSectorItems } from "@/utils/filter";
import {
  getEconomyDisplayName,
  makePossessive,
  formatPercentage,
  getSectorDisplayName,
} from "@/utils/format";
import colors from "@/config/colors";
import placeholderMap from "@/config/map/geo.json";
import kashmir from "@/config/map/kashmir.json";
import { getKeys } from "@/utils/keys";
import type { Feature, FeatureCollection } from "geojson";
import { Slider } from "../Slider";
import styles from "./styles.module.scss";

const width = 1200;
const height = 650;

const path = d3.geoPath();
const projection = geoRobinson()
  .fitSize([width, height], placeholderMap as unknown as FeatureCollection)
  .translate([width / 2, height / 2])
  .center([15, 10]);

const kashmirCentroid = path
  .projection(projection)
  .centroid(kashmir as Feature);

const kashmirBbox = path.projection(projection).bounds(kashmir as Feature);
const kashmirBoxSize =
  kashmirBbox.reduce((longest, coord) => {
    const max = Math.max(
      Math.abs(coord[0] - kashmirCentroid[0]),
      Math.abs(coord[1] - kashmirCentroid[1])
    );
    if (max > longest) {
      return max;
    }

    return longest;
  }, 0) * 2;

const features = placeholderMap.features.map((feature) => ({
  adm0_a3: feature.properties.adm0_a3,
  path: path.projection(projection)(feature as Feature) || "",
}));

const sectorItems = getSectorItems(sectorOptions);

const otherColor = colors.white17;
const noDataColor = colors.white05;
const tradingPartnerColors = {
  USA: colors.cyan500,
  EUE: colors.electricBlue500,
  CHN: colors.crimsonRed300,
  Other: otherColor,
};

const isTradingPartnerColorKey = (
  value?: string
): value is keyof typeof tradingPartnerColors =>
  value ? value in tradingPartnerColors : false;

interface DataRow {
  "Reporter code": string;
  "Sector code": string;
  "Partner code": string;
  Year: string;
}

type DataByReporterCode = {
  [reporterCode: string]: { [year: string]: string };
};

export function Map() {
  const [data, setData] = useState<DataByReporterCode>();
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  const prefersReducedMotion = usePrefersReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstLoad = useRef(true);

  const yearOptions = useMemo(() => {
    if (!data) {
      return [];
    }

    const yearOptionsSet = new Set<string>();
    Object.keys(data).forEach((reporterCode) => {
      Object.keys(data[reporterCode] || {}).forEach((year) => {
        yearOptionsSet.add(year);
      });
    }, null);
    return Array.from(yearOptionsSet).sort((a, b) => parseInt(a) - parseInt(b));
  }, [data]);

  const [year, setYear] = useState<string>(yearOptions[0]);

  const { value: sector, onChange: onSectorChange } = useFilter(
    "sector",
    sectorOptions
  );

  const displaySector = getSectorDisplayName(sector);

  useEffect(() => {
    setIsLoadingData(true);

    d3.csv(`${process.env.ASSET_PATH || ""}/data/map/${sector}.csv`)
      .then((data: unknown) => {
        const dataByReporterCode = (
          data as DataRow[]
        ).reduce<DataByReporterCode>((byReporterCode, row) => {
          byReporterCode[row["Reporter code"]] = {
            ...byReporterCode[row["Reporter code"]],
            [row.Year]: row["Partner code"],
          };
          return byReporterCode;
        }, {});

        setData(dataByReporterCode);

        const dataYears = Array.from(
          (data as DataRow[]).reduce((uniqueYears, row) => {
            uniqueYears.add(row.Year);
            return uniqueYears;
          }, new Set<string>())
        ).sort((a, b) => parseInt(a) - parseInt(b));

        setYear((y) => {
          if (!dataYears.includes(y)) {
            return dataYears[0];
          }
          return y;
        });

        if (isFirstLoad.current) {
          setIsPlaying(!prefersReducedMotion);
          isFirstLoad.current = false;
        } else {
          setIsPlaying(false);
        }
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  }, [sector, prefersReducedMotion]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    if (isPlaying) {
      interval = setInterval(() => {
        setYear((y) => {
          const nextYear = parseInt(y) + 1;
          if (yearOptions.includes(nextYear.toString())) {
            return nextYear.toString();
          } else {
            setIsPlaying(false);
            clearInterval(interval);
            return y;
          }
        });
      }, 750);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [yearOptions, isPlaying]);

  const is768px = useMediaQuery("(min-width: 768px)");

  const {
    showTooltip,
    moveTooltip,
    hideTooltip,
    tooltipData,
    tooltipProps,
    tooltipVisible,
  } = useTooltip<string>(containerRef.current);

  const [chinaCount, euCount, usaCount] = Object.keys(data || {}).reduce(
    (counts, reporterCode) => {
      if (data?.[reporterCode][year] === "CHN") {
        counts[0]++;
      } else if (data?.[reporterCode][year] === "EUE") {
        counts[1]++;
      } else if (data?.[reporterCode][year] === "USA") {
        counts[2]++;
      }

      return counts;
    },
    [0, 0, 0]
  );
  const numEconomies = Object.keys(data || {}).length;

  const eueDisplayName = getEconomyDisplayName("EUE");
  const usaDisplayName = getEconomyDisplayName("USA");
  const chnDisplayName = getEconomyDisplayName("CHN");

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles["chart-wrap"]}>
        {data && !isLoadingData ? (
          <span className={styles.hide}>
            {`The three major global trading partners are ${chnDisplayName}, ${eueDisplayName}, and ${usaDisplayName}. For ${displaySector.toLowerCase()}, in ${year}, ${formatPercentage(
              chinaCount / numEconomies
            )} of the world's top trading partner was ${chnDisplayName}, ${formatPercentage(
              euCount / numEconomies
            )} was ${eueDisplayName}, and ${formatPercentage(
              usaCount / numEconomies
            )} was ${usaDisplayName}.`}
          </span>
        ) : null}

        <header className={styles.header}>
          <div>
            <h3 className={styles["header-heading"]}>
              Top trading partner of each economy
            </h3>
            <div className={styles["legend"]}>
              {getKeys(tradingPartnerColors).map((tradingPartner) => (
                <div
                  data-partner={tradingPartner}
                  key={tradingPartner}
                  className={styles["legend-row"]}
                >
                  <div
                    className={styles["legend-color"]}
                    style={
                      {
                        "--trading-partner-color":
                          tradingPartnerColors[tradingPartner],
                      } as React.CSSProperties
                    }
                  />
                  <p>{getEconomyDisplayName(tradingPartner)}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className={styles.controls}>
              {data && !isLoadingData ? (
                <>
                  <div className={styles["controls-row"]}>
                    <div className={styles["sector-selector"]}>
                      <Select
                        label="Explore by sector"
                        format="auto-width"
                        items={sectorItems}
                        value={sector}
                        onChange={(value) => {
                          onSectorChange(value);
                        }}
                      />
                    </div>

                    <div className={styles["button-year"]}>
                      <button
                        type="button"
                        className={styles["playback-button"]}
                        aria-label={isPlaying ? "Pause" : "Play"}
                        onClick={() => {
                          if (
                            !isPlaying &&
                            year === yearOptions[yearOptions.length - 1]
                          ) {
                            setYear(yearOptions[0]);
                          }
                          setIsPlaying((prev) => !prev);
                        }}
                      >
                        {isPlaying ? (
                          <svg
                            className={styles["playback-svg-pause"]}
                            fill="currentColor"
                          >
                            <use xlinkHref="#pause" />
                          </svg>
                        ) : (
                          <svg className={styles["playback-svg-play"]}>
                            <use xlinkHref="#play" />
                          </svg>
                        )}
                      </button>

                      <p className={styles["playback-year"]}>{year}</p>
                    </div>
                  </div>

                  <div
                    className={styles.playback}
                    onMouseEnter={() => {
                      hideTooltip();
                    }}
                  >
                    <div className={styles["playback-track-and-toggle"]}>
                      <Slider
                        value={year}
                        onChange={(y) => {
                          setYear(y);
                          setIsPlaying(false);
                        }}
                        min={yearOptions[0]}
                        max={yearOptions[yearOptions.length - 1]}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles["playback-loading"]} />
              )}
            </div>
          </div>
        </header>

        <svg
          viewBox={`0 0 ${width} ${height}`}
          className={clsx(styles.svg, isLoadingData && styles.loading)}
          style={{ aspectRatio: `${width} / ${height}` }}
          aria-hidden
        >
          <g className={styles["svg-content"]}>
            {features.map((feature) => {
              const tradingPartner =
                data && feature.adm0_a3 in data
                  ? data[feature.adm0_a3 || ""][year]
                  : undefined;
              const color = isLoadingData
                ? otherColor
                : isTradingPartnerColorKey(tradingPartner)
                ? tradingPartnerColors[tradingPartner]
                : tradingPartner
                ? otherColor
                : noDataColor;

              return (
                <g
                  key={feature.adm0_a3}
                  className={styles.economy}
                  data-partner={
                    isLoadingData
                      ? "loading"
                      : color == otherColor
                      ? "Other"
                      : color === noDataColor
                      ? "No Data"
                      : tradingPartner
                  }
                >
                  <path
                    d={feature.path}
                    fill={color}
                    onMouseEnter={
                      isLoadingData
                        ? undefined
                        : (event) => {
                            showTooltip(event, feature.adm0_a3);
                          }
                    }
                    onMouseMove={
                      isLoadingData
                        ? undefined
                        : (event) => {
                            moveTooltip(event);
                          }
                    }
                    onMouseLeave={
                      isLoadingData
                        ? undefined
                        : () => {
                            hideTooltip();
                          }
                    }
                  />
                </g>
              );
            })}
            <g>
              <rect
                x={kashmirCentroid[0] - kashmirBoxSize / 2}
                y={kashmirCentroid[1] - kashmirBoxSize / 2}
                width={kashmirBoxSize}
                height={kashmirBoxSize}
                className={styles["contested-marker"]}
              />
              <text
                x={kashmirCentroid[0]}
                y={kashmirCentroid[1]}
                textAnchor="middle"
                dominantBaseline="central"
                className={styles["contested-text"]}
              >
                1
              </text>
            </g>
          </g>
        </svg>
      </div>

      {tooltipVisible && tooltipData ? (
        <Tooltip
          {...tooltipProps}
          style={{ ...tooltipProps.style, width: is768px ? 300 : 220 }}
        >
          <div className={styles.tooltip}>
            <div className={styles["tooltip-row"]}>
              <p className={styles["tooltip-economy"]}>
                {makePossessive(getEconomyDisplayName(tooltipData))} top trading
                partner
              </p>
              <p className={styles["tooltip-year"]}>{year}</p>
            </div>
            <p className={styles["tooltip-partner"]}>
              {data?.[tooltipData]?.[year]
                ? getEconomyDisplayName(data?.[tooltipData]?.[year])
                : "No data available"}
            </p>
          </div>
        </Tooltip>
      ) : null}
    </div>
  );
}
